import { supabaseImageVariant } from "@/lib/supabase-image";

/** Blog body column is max-w-3xl (≈736px content) → ~2x = 828 (→ -w960). */
export const BLOG_BODY_IMAGE_WIDTH = 828;

/**
 * Product detail column: max-w-7xl minus padding, the 400px sidebar and the
 * gap ≈ 640px on desktop; 100vw on mobile (≈430px × 3 DPR) → 1280 covers both.
 * Above the largest variant (960), so detail images use the original object.
 */
export const DETAIL_BODY_IMAGE_WIDTH = 1280;

/**
 * Static fallback handler — the original URL travels in `data-fallback-src`
 * (the attribute value exactly as the editor wrote it) and is never
 * interpolated into JS, so entities like `&#39;` in a URL can't break out.
 */
const FALLBACK_ONERROR =
  'onerror="this.onerror=null;this.src=this.dataset.fallbackSrc"';

/**
 * Rewrites every `<img>` in editor-authored HTML (TipTap output stored as
 * blog `body_html` / product `detail_page_html`) so Supabase Storage sources
 * point at the pre-generated `-w480/-w960.webp` variant for `width` instead of
 * the upload-resolution original. Non-storage sources pass through; widths
 * above 960 keep the original.
 *
 * Variants may not exist yet (backfill pending / upload path without
 * variants), and this HTML is injected raw, so no React onError is available.
 * When the src is swapped, the original goes into `data-fallback-src` and a
 * static inline `onerror` reads it back, so a 404 falls back to the original
 * once (the site CSP has no script-src, so inline handlers run;
 * `this.onerror=null` prevents loops).
 *
 * `loading="lazy"` / `decoding="async"` are added only when the editor did not
 * emit them, so an author's explicit choice is respected.
 *
 * @param width Target width in px — ~2x the rendered column width.
 */
export function optimizeBodyImages(html: string, width: number): string {
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    // An author-supplied onerror can't be combined with ours → keep original.
    const hasOwnOnError = /\sonerror=/i.test(tag);
    let fallbackSrc: string | null = null;
    let next = tag.replace(
      /(\ssrc=)(["'])([^"']+)\2/i,
      (match, prefix: string, quote: string, src: string) => {
        const variant = supabaseImageVariant(src, width);
        if (variant === src || hasOwnOnError) return match;
        fallbackSrc = src;
        return `${prefix}${quote}${variant}${quote}`;
      },
    );
    if (fallbackSrc !== null) {
      next = next.replace(
        /^<img\b/i,
        `<img ${FALLBACK_ONERROR} data-fallback-src="${fallbackSrc}"`,
      );
    }
    if (!/\sloading=/i.test(next)) {
      next = next.replace(/^<img\b/i, '<img loading="lazy"');
    }
    if (!/\sdecoding=/i.test(next)) {
      next = next.replace(/^<img\b/i, '<img decoding="async"');
    }
    return next;
  });
}
